import { useLayoutEffect, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useEntityFeatures } from '@/hooks/use-entity-features';
import { EntityFeaturesContext } from '@/features/tenancy/entity-features-context';

/** Retire team-only readers and their cached results when admission is uncertain or revoked. */
export function TeamFeatureBoundary({ children, fallback = null }: { children: ReactNode; fallback?: ReactNode }) {
  const features = useEntityFeatures();
  const client = useQueryClient();
  const admitted = !features.tenantSlug || (!features.isPending && !features.isError &&
    !features.isFetching && !features.isPlaceholderData && features.isEnabled('teams_and_colleagues'));
  useLayoutEffect(() => {
    if (admitted) return;
    const predicate = (query: { queryKey: readonly unknown[] }) => query.queryKey.some(key =>
      typeof key === 'string' && (/^\/api\/(workgroups|colleagues|manager\/attendance)(?:\/|$)/.test(key) ||
        /^(team-desk-|desk-hold|desk-holds|desk-groups|named-desks|named-desk-receipt)/.test(key)));
    void client.cancelQueries({ predicate });
    client.removeQueries({ predicate });
  }, [admitted, client]);
  return <EntityFeaturesContext.Provider value={features}>{admitted ? children : fallback}</EntityFeaturesContext.Provider>;
}
